/**
 * AI:
 * Status: 'in progress'
 * Type: Service
 * SubType: WidgetConfig
 * Reason: Configures the entity/list data provider of the bd.DunningType child entity table —
 *         dataSource 'entity', dataProvider 'list', dataProviderObject taken from the descriptor,
 *         with an ObjectRequestList filtered on the 'dunningType' FK.
 */
import {Component, Input, OnChanges, OnInit, SimpleChanges} from '@angular/core';
import {WidgetData} from 'features/core/shared/widget/widget-data';
import {WidgetFactory} from 'features/core/shared/widget/service/widget.factory';
import {WidgetDataParam} from 'features/core/shared/widget/widget-data-param';
import {WidgetToolbarCallbackInterface} from 'features/core/shared/widget/widget-toolbar-callback.interface';
import {ObjectRequestList} from 'features/core/shared/dto/object/request/object-request-list';
import {FilterCriteria} from 'features/core/shared/filter/api/filter.criteria';
import {DtoDetail} from 'features/core/shared/dto/dto.detail';
import {DtoImportObjectContext} from 'features/core/shared/dto/dto.import.object.context';
import {DtoImportObjectContextEntry} from 'features/core/shared/dto/dto.import.object.context.entry';
import {MvsFormControlOverwrite} from 'features/core/shared/logic/form/logic/mvs-form-control-overwrite';
import {MvsFormFieldAccessEnum} from 'features/core/shared/logic/form/logic/mvs-form-field-access.enum';
import {EntityStatusEnum} from 'features/core/shared/entity/enum/entity-status.enum';
import {BdDunningTypeChildTable} from '../../../../../interface/bd-dunning-type-child-table.interface';

/**
 * AI:
 * Status: 'in progress'
 * Type: Component
 * SubType: Config / Child-Table
 * Reason: Renders ONE bd.DunningType child entity as a framework table widget, driven by a
 *         BdDunningTypeChildTable descriptor. Replaces the eight hand wired child widgets on the
 *         DunningType OBC and the two hand rolled reason editors with a single data driven
 *         component, so FK pre-fill, status handling and the create button are defined once
 *         instead of ten times.
 *
 * <h3>Why the descriptor is an input and not ten components</h3>
 * All ten child entities are the same shape: a 1:N list hanging off the same FK ('dunningType').
 * The only things that differ are the alias, the German title, the entity alias, the German empty
 * text and the sort order — i.e. exactly the descriptor. The OBC owns the map from navigation item
 * to descriptor; this component owns the widget.
 */
@Component({
    selector: 'bd-dunning-type-child-table',
    templateUrl: './bd-dunning-type-child-table.component.html',
    styleUrls: ['./bd-dunning-type-child-table.component.scss'],
    standalone: false
})
export class BdDunningTypeChildTableComponent implements OnInit, OnChanges {

    /** Id of the DunningType whose children are listed. */
    @Input() dunningTypeId: number;

    /** Descriptor of the child entity to render. */
    @Input() child: BdDunningTypeChildTable;

    /** The table widget, null while the inputs are incomplete (template renders a skeleton). */
    childWidget: WidgetData;

    /** Import context so the create/edit dialog knows which DunningType it is working under. */
    importContext: DtoImportObjectContext;

    initialized = false;

    ngOnInit(): void {
        this.refreshWidget();
        this.initialized = true;
    }

    /**
     * Rebuild on EVERY change of either input.
     *
     * Mandatory, not defensive: the OBC renders the child tables from one `@else if` branch, so
     * Angular REUSES this single component instance across navigation items and only rebinds the
     * inputs. Without the rebuild the user would click „Stufen“ and keep looking at the
     * „Startbedingungen“ table — same widget, same alias, same data.
     */
    ngOnChanges(changes: SimpleChanges): void {

        if (!this.initialized) {
            // first binding — ngOnInit builds, no need to build twice
            return;
        }

        if (changes['child'] || changes['dunningTypeId']) {
            this.refreshWidget();
        }
    }

    /**
     * Build the table widget for the currently bound descriptor.
     */
    protected refreshWidget(): void {

        this.childWidget = null;
        this.importContext = null;

        if (!this.child || this.dunningTypeId == null) {
            return;
        }

        const child: BdDunningTypeChildTable = this.child;
        const dunningTypeId: number = this.dunningTypeId;

        /**
         * AI:
         * Status: 'in progress'
         * Type: Widget
         * SubType: Runtime
         * Reason: Table of one bd.DunningType child entity (levels, charges, starts, stops,
         *         termination reasons, ...) filtered to the DunningType currently open, with the
         *         quick add button enabled so the entity can be maintained from its navigation item.
         *
         * 'formQuickAdd' must be passed explicitly: the table widget type declares it with
         * defaultValue false (widget-type-provider-implementation-table.ts:186), so without it the
         * table renders without a „+“ button and the entity would be read only.
         */
        this.childWidget = WidgetFactory.createWidgetTableEntity(
            child.alias,
            child.label,
            child.entity,
            child.noDataText,
            ObjectRequestList.createBasic(
                true,
                [FilterCriteria.create('dunningType', FilterCriteria.cOperatorEqual, dunningTypeId)],
                child.sorting ?? []
            ),
            WidgetDataParam.create('formQuickAdd', true)
        );

        this.childWidget.functionCallbacks = {
            onFunctionCreateNew: (): WidgetToolbarCallbackInterface => this.buildCreateCallback(child, dunningTypeId)
        };

        this.importContext = new DtoImportObjectContext(
            [new DtoImportObjectContextEntry('bd.DunningType', dunningTypeId)]
        );
    }

    /**
     * Default dto plus form control overwrite for a new child row.
     *
     * <h3>Why this is written out instead of using WidgetFunctionCallBackCreate.widgetToolbarCreate</h3>
     * That helper locks EVERY field it pre-fills (widget-function-call-back-create.ts:19). Here the
     * pre-filled set and the locked set are deliberately DIFFERENT: the FK is locked because the row
     * belongs to the DunningType the user opened and may not be moved to another one, while the
     * status is only a default and has to stay editable.
     *
     * <h3>Why only the FK may be locked</h3>
     * mvs-widget-table.handleEdit() reads this same formControlOverwrite and applies it to the EDIT
     * dialog as well (mvs-widget-table.component.ts:1369). Anything locked here is therefore locked
     * for the whole lifetime of the row, not just at creation — correct for the FK, wrong for
     * everything else.
     *
     * <h3>Why there is NO value list filter on entityStatus</h3>
     * Do not try to narrow the status picker to active/obsolete via
     * addFieldValueListFilter(). The overwrite returned here is reused by the EDIT dialog (see
     * above), and MvsFormControlService.applyFieldValueListFilter() REMOVES the non-allowed entries
     * from the value list (mvs-form-control.service.ts:770-788) rather than merely defaulting away
     * from them. Legacy reason rows carry entityStatus = draft — written by the custom editors this
     * component replaced — so editing one would render a status field whose own current value is not
     * among the offered entries, i.e. a blank, mandatory looking picker. The callback cannot see the
     * row being edited, so a create-only filter is not expressible here.
     *
     * The pre-fill below already delivers the business rule ('a new reason is active'), and it is
     * the half that does NOT leak into edit, because handleEdit() reads only formControlOverwrite
     * and ignores the dto.
     */
    protected buildCreateCallback(
        child: BdDunningTypeChildTable,
        dunningTypeId: number): WidgetToolbarCallbackInterface {

        const dto = new DtoDetail();
        dto['dunningTypeDtoId'] = dunningTypeId;

        const overwrite = new MvsFormControlOverwrite();
        overwrite.addField('dunningTypeDtoId', MvsFormFieldAccessEnum.READ);

        if (child.defaultActiveStatus) {
            // Pre-fill only — deliberately NOT added to the overwrite, so the status stays editable
            // and the CREATE dialog opens on the one value the backend acts on.
            dto['entityStatus'] = EntityStatusEnum.active;
        }

        return {
            dto: dto,
            formControlOverwrite: overwrite
        };
    }

}
